import React, { useEffect, useRef, useState } from 'react';
import { Send, Square, AlertCircle, Copy, Check, NotebookPen, Mic, MicOff, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Logo } from './Logo';
import { streamDifyChat, isDifyConfigured } from '../../lib/difyClient';
import { storage, addXP, XP_PER_MESSAGE, XP_PER_NOTE } from '../../lib/storage';
import { canConsume, consume, getCurrentTier } from '../../lib/quota';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { toast } from 'sonner';

const SUGGESTIONS = [
  'रस के प्रकार और उनके स्थायी भाव विस्तार से समझाओ',
  'छायावाद युग के प्रमुख कवि और विशेषताएँ बताओ',
  'अलंकार और रस में अंतर उदाहरण सहित समझाओ',
  'गोदान उपन्यास का सारांश और प्रमुख पात्र',
  'भारतीय काव्यशास्त्र के सम्प्रदाय (रस, ध्वनि, वक्रोक्ति, अलंकार, औचित्य)',
];

export const ChatWorkspace = ({ chat, onUpdateChat, guruId, onFirstMessage }) => {
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [voiceLang, setVoiceLang] = useState('hi-IN');
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const baseTextRef = useRef('');
  const messages = chat?.messages || [];

  const { listening, supported: voiceSupported, start: startVoice, stop: stopVoice } = useSpeechRecognition({
    lang: voiceLang,
    onResult: ({ combined }) => {
      setInput(`${baseTextRef.current}${baseTextRef.current ? ' ' : ''}${combined}`);
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, streaming, chat?.id]);

  const apiKey = process.env.REACT_APP_DIFY_API_KEY;
  const configured = isDifyConfigured(apiKey);

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;

    if (!canConsume('chats')) {
      const tier = getCurrentTier();
      toast.error('Daily chat limit reached', {
        description: `${tier.label} allows ${tier.chats}/day. Upgrade Prime for unlimited.`,
      });
      return;
    }
    consume('chats');

    // Notify parent on first message of this chat (so it can collapse sidebar)
    if (messages.length === 0) onFirstMessage?.();

    const userMsg = { id: `m_${Date.now()}`, role: 'user', content: text, ts: Date.now() };
    const assistantMsg = { id: `m_${Date.now() + 1}`, role: 'assistant', content: '', ts: Date.now() + 1, streaming: true };

    const newMessages = [...messages, userMsg, assistantMsg];
    const title = chat.title === 'New Chat' || !chat.title ? text.slice(0, 38) : chat.title;
    onUpdateChat({ ...chat, title, messages: newMessages, guruId });
    setInput('');
    setStreaming(true);

    if (!configured) {
      const demoText = `🪔 *Demo Mode* — Dify API key not configured.\n\nAap ne poochha: "${text}"\n\nProduction mode mein, yeh response Dify AI se aayega.`;
      let i = 0;
      const interval = setInterval(() => {
        i += 3;
        onUpdateChat({
          ...chat, title,
          messages: [...messages, userMsg, { ...assistantMsg, content: demoText.slice(0, i) }],
        });
        if (i >= demoText.length) {
          clearInterval(interval);
          onUpdateChat({
            ...chat, title,
            messages: [...messages, userMsg, { ...assistantMsg, content: demoText, streaming: false }],
          });
          addXP(XP_PER_MESSAGE);
          window.dispatchEvent(new Event('brahmastra:xp'));
          setStreaming(false);
        }
      }, 16);
      abortRef.current = { abort: () => { clearInterval(interval); setStreaming(false); } };
      return;
    }

    const ac = new AbortController();
    abortRef.current = ac;
    let answerSoFar = '';
    let convId = chat.conversationId || '';

    try {
      await streamDifyChat({
        apiKey, query: text, conversationId: convId, signal: ac.signal,
        onEvent: (ev) => {
          if (ev.conversation_id) convId = ev.conversation_id;
          if (ev.event === 'message' || ev.event === 'agent_message') {
            answerSoFar += ev.answer || '';
            onUpdateChat({
              ...chat, title, conversationId: convId,
              messages: [...messages, userMsg, { ...assistantMsg, content: answerSoFar }],
            });
          } else if (ev.event === 'error') {
            throw new Error(ev.message || 'Dify error');
          }
        },
      });
      onUpdateChat({
        ...chat, title, conversationId: convId,
        messages: [...messages, userMsg, { ...assistantMsg, content: answerSoFar || '...', streaming: false }],
      });
      addXP(XP_PER_MESSAGE);
      window.dispatchEvent(new Event('brahmastra:xp'));
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error(e);
      toast.error('Chat failed', { description: String(e.message || e) });
      onUpdateChat({
        ...chat, title,
        messages: [...messages, userMsg, {
          ...assistantMsg,
          content: `⚠️ Error: ${e.message}. Apni Dify API key check karein.`,
          streaming: false, error: true,
        }],
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stop = () => { abortRef.current?.abort?.(); setStreaming(false); };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const toggleVoice = () => {
    if (listening) { stopVoice(); return; }
    if (!voiceSupported) {
      toast.error('Voice input not supported', { description: 'Please use Chrome browser.' });
      return;
    }
    baseTextRef.current = input;
    startVoice();
    toast.message('🎤 Listening...', { description: `Lang: ${voiceLang}`, duration: 1500 });
  };

  return (
    <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-app relative overflow-x-hidden" data-testid="chat-workspace">
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll" ref={scrollRef} data-testid="messages-area">
        <div className="max-w-full mx-auto px-3 sm:px-6 py-8">
          {messages.length === 0 ? (
            <EmptyState onPick={(t) => sendMessage(t)} configured={configured} />
          ) : (
            <div className="space-y-5">
              {messages.map((m) => (
                <MessageBubble key={m.id} msg={m} />
              ))}
              {streaming && (
                <div className="flex items-center gap-2 text-muted-foreground text-xs pl-12">
                  <span className="dot-pulse"><span /><span /><span /></span>
                  Guru ji soch rahe hain...
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-border bg-card/60 glass sticky bottom-0 left-0 right-0 z-20" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="max-w-full mx-auto px-3 sm:px-6 py-2">
          {!configured && (
            <div className="mb-2 flex items-center gap-2 text-[11px] text-amber-400/90" data-testid="dify-warning">
              <AlertCircle className="w-3.5 h-3.5" />
              Demo mode — set REACT_APP_DIFY_API_KEY in <code className="mono px-1 rounded bg-secondary">frontend/.env</code> to enable live AI.
            </div>
          )}
          <div className={`flex flex-wrap items-end gap-2 p-2 rounded-2xl bg-secondary/60 border ${listening ? 'border-mint/60 glow-mint' : 'border-border focus-within:border-mint/60'} transition`}>
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={listening ? '🎤 Bolen... aap ki awaaz sun raha hoon' : 'Apna sawaal yahan likhein... (Shift+Enter = newline)'}
              className="flex-1 resize-none bg-transparent outline-none text-[15px] leading-6 placeholder:text-muted-foreground/70 max-h-28 min-h-[3rem] px-3 py-2"
              style={{ minHeight: 40, touchAction: 'manipulation' }}
            />
            {voiceSupported && (
              <button
                onClick={() => setVoiceLang(l => l === 'hi-IN' ? 'en-IN' : 'hi-IN')}
                data-testid="voice-lang-toggle"
                className="h-9 px-2 rounded-lg text-[10px] font-bold bg-background/50 border border-border hover:border-mint/40 text-muted-foreground hover:text-mint transition"
                title={`Voice language: ${voiceLang}`}
              >
                {voiceLang === 'hi-IN' ? 'हिं' : 'EN'}
              </button>
            )}
            <button
              onClick={toggleVoice}
              data-testid="voice-btn"
              disabled={streaming}
              className={`h-9 w-9 rounded-xl flex items-center justify-center transition ${
                listening
                  ? 'bg-destructive text-white animate-pulse'
                  : 'bg-background/60 border border-border hover:border-mint/40 text-muted-foreground hover:text-mint'
              }`}
              aria-label="Voice input"
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            {streaming ? (
              <button onClick={stop} data-testid="stop-btn" className="h-9 w-9 rounded-xl bg-destructive/90 hover:bg-destructive text-white flex items-center justify-center transition">
                <Square className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => sendMessage()}
                data-testid="send-btn"
                disabled={!input.trim()}
                className="h-10 w-10 rounded-xl bg-mint text-[#08120e] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition glow-mint"
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="text-center text-[10px] text-muted-foreground/70 mt-2 opacity-80">
            Brahmastra AI may produce inaccurate info. Verify important facts.
          </div>
        </div>
      </div>
    </main>
  );
};

const MessageBubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  const [copied, setCopied] = useState(false);

  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(msg.content);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed');
    }
  };

  const saveToDiary = () => {
    const notes = storage.get(storage.KEYS.NOTES, []);
    const firstLine = msg.content.split('\n').find(l => l.trim()) || 'Saved from chat';
    const note = {
      id: `n_${Date.now()}`,
      title: firstLine.replace(/[*_`#]/g, '').slice(0, 60),
      body: msg.content,
      ts: Date.now(),
      source: 'chat',
    };
    storage.set(storage.KEYS.NOTES, [note, ...notes]);
    addXP(XP_PER_NOTE);
    window.dispatchEvent(new Event('brahmastra:xp'));
    toast.success('Saved to Diary', { description: `+${XP_PER_NOTE} XP · Open sidebar > My Diary` });
  };

  return (
    <div className={`flex gap-3 anim-fade-up group ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`msg-${msg.role}`}>
      {isUser ? (
        <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center text-[12px] font-bold bg-bblue/15 text-bblue border border-bblue/30">
          You
        </div>
      ) : (
        <div className="shrink-0"><Logo size="sm" testId="msg-logo" /></div>
      )}
      <div className={`max-w-[92%] sm:max-w-[80%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        <div className={`rounded-2xl px-4 py-3 chat-bubble text-[14px] leading-6 ${
          isUser
            ? 'bg-bblue/10 border border-bblue/20 text-foreground'
            : msg.error
            ? 'bg-destructive/10 border border-destructive/30'
            : 'bg-card border border-border'
        }`}>
          {isUser ? (
            msg.content.split('\n').map((line, i) => <p key={i}>{line || '\u00A0'}</p>)
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                strong: ({ children }) => <strong className="font-bold text-mint">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                code: ({ inline, children }) => inline
                  ? <code className="bg-secondary px-1 rounded text-[12.5px]">{children}</code>
                  : <pre className="bg-secondary p-2 rounded overflow-auto text-[12.5px]"><code>{children}</code></pre>,
                ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                h1: ({ children }) => <h1 className="text-lg font-bold mt-2 mb-1">{children}</h1>,
                h2: ({ children }) => <h2 className="text-[15px] font-bold mt-2 mb-1">{children}</h2>,
                h3: ({ children }) => <h3 className="text-[14px] font-semibold mt-1.5 mb-1">{children}</h3>,
                a: ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" className="text-bblue underline">{children}</a>,
              }}
            >
              {msg.content}
            </ReactMarkdown>
          )}
        </div>
        {!isUser && !msg.streaming && msg.content && (
          <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition" data-testid="msg-actions">
            <button
              onClick={copyMsg}
              data-testid={`copy-${msg.id}`}
              className="text-[10.5px] flex items-center gap-1 px-2 py-1 rounded-md bg-secondary/40 border border-border text-muted-foreground hover:text-mint hover:border-mint/30 transition"
            >
              {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
            <button
              onClick={saveToDiary}
              data-testid={`save-diary-${msg.id}`}
              className="text-[10.5px] flex items-center gap-1 px-2 py-1 rounded-md bg-secondary/40 border border-border text-muted-foreground hover:text-bblue hover:border-bblue/30 transition"
            >
              <NotebookPen className="w-3 h-3" /> Save to Diary
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState = ({ onPick, configured }) => (
  <div className="text-center pt-6 sm:pt-10">
    <div className="inline-block"><Logo size="md" /></div>
    <h1 className="mt-4 text-xl sm:text-2xl font-bold tracking-tight">
      Namaste! <span className="text-mint">Brahmastra AI</span> mein swagat hai
    </h1>
    <p className="mt-1.5 text-muted-foreground text-[12.5px] max-w-md mx-auto">
      <span className="text-mint font-medium">Hindi Sahitya Guru</span> — for UGC NET / TGT / PGT exam prep.
    </p>
    {!configured && (
      <div className="mt-2.5 inline-block text-[10.5px] text-amber-400/90 bg-amber-400/10 border border-amber-400/20 rounded-full px-3 py-0.5">
        Demo mode active
      </div>
    )}
    <div className="mt-6 max-w-2xl mx-auto text-left space-y-1">
      <div className="text-[10.5px] uppercase tracking-widest text-muted-foreground/70 px-3 mb-1">Try asking</div>
      {SUGGESTIONS.map((s, i) => (
        <button
          key={i}
          onClick={() => onPick(s)}
          data-testid={`suggestion-${i}`}
          className="group w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/60 transition text-left"
        >
          <span className="text-[13.5px] devanagari text-foreground/90 group-hover:text-foreground line-clamp-1">{s}</span>
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/60 group-hover:text-mint transition translate-x-0 group-hover:translate-x-0.5" />
        </button>
      ))}
    </div>
  </div>
);

export default ChatWorkspace;
